import { useState } from 'react';
import {
  useGetKfoReconcileQuery,
  useGetKfoSummaryQuery,
  type KfoRecItem,
  type KfoSummaryGroup,
} from '@shared/api';
import {
  Banner,
  DataTable,
  Muted,
  Panel,
  PanelTitle,
  Stat,
  Stats,
  Tab,
  Tabs,
  TableWrap,
  errorText,
  fmtNum,
  isNotFound,
} from './ui';

const GROUP_LABEL: Record<KfoSummaryGroup, string> = {
  titul: 'Титул',
  zone: 'Зона',
  work_kind: 'Вид работ',
  contract: 'Договор',
};

const GROUP_TAB: Record<KfoSummaryGroup, string> = {
  titul: 'по титулам',
  zone: 'по зонам',
  work_kind: 'по видам работ',
  contract: 'по договорам',
};

/**
 * Сводка по группе (титул / зона / вид работ / договор) и единице измерения,
 * плюс сверка нашего учёта с Заказчиком (этап 2, §9). Пока сервер не отдаёт
 * эти эндпоинты (404) — вкладка объясняет, что раздел в работе.
 */
export function KfoSummaryTab({
  projectId,
  onOpenLine,
}: {
  projectId: string;
  onOpenLine: (lineId: string) => void;
}) {
  const [group, setGroup] = useState<KfoSummaryGroup>('titul');
  const {
    data: summary,
    isFetching,
    error: summaryError,
  } = useGetKfoSummaryQuery({ projectId, group }, { skip: !projectId });
  const {
    data: rec,
    error: recError,
    isLoading: recLoading,
  } = useGetKfoReconcileQuery(projectId, {
    skip: !projectId,
  });

  const t = summary?.totals;

  return (
    <div>
      <Panel>
        <PanelTitle>Сводка</PanelTitle>
        <Tabs style={{ marginBottom: 12 }}>
          {(Object.keys(GROUP_LABEL) as KfoSummaryGroup[]).map((g) => (
            <Tab key={g} type="button" $active={g === group} onClick={() => setGroup(g)}>
              {GROUP_TAB[g]}
            </Tab>
          ))}
        </Tabs>

        {isNotFound(summaryError) ? (
          <Banner>Сводка появится после обновления сервера (этап 2 раздела КФО).</Banner>
        ) : summaryError ? (
          <Banner $tone="danger">{errorText(summaryError, 'Не удалось построить сводку.')}</Banner>
        ) : (
          <>
            {t && (
              <Stats>
                <Stat>
                  <b>{t.lines.toLocaleString('ru-RU')}</b>
                  <span>строк КФО</span>
                </Stat>
                <Stat>
                  <b>{t.absent.toLocaleString('ru-RU')}</b>
                  <span>нет в срезе</span>
                </Stat>
                <Stat>
                  <b>{t.with_rfi.toLocaleString('ru-RU')}</b>
                  <span>с RFI</span>
                </Stat>
                <Stat>
                  <b>{t.guarantees_open.toLocaleString('ru-RU')}</b>
                  <span>ГО открыто</span>
                </Stat>
                <Stat>
                  <b style={{ color: t.guarantees_overdue > 0 ? 'crimson' : undefined }}>
                    {t.guarantees_overdue.toLocaleString('ru-RU')}
                  </b>
                  <span>ГО просрочено</span>
                </Stat>
              </Stats>
            )}
            <TableWrap $maxHeight="52vh" style={{ opacity: isFetching ? 0.6 : 1 }}>
              <DataTable>
                <thead>
                  <tr>
                    <th>{GROUP_LABEL[group]}</th>
                    <th>Ед.</th>
                    <th className="num">Строк</th>
                    <th className="num">ФО по РД</th>
                    <th className="num">Факт накоп.</th>
                    <th className="num">Принято СК</th>
                    <th className="num">ИД принято</th>
                    <th className="num">ПА</th>
                    <th className="num">НЗП</th>
                    <th className="num">Выполнено, %</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.rows ?? []).length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', padding: 24 }}>
                        <Muted>
                          {isFetching ? 'Считаю…' : 'Нет данных. Загрузите срез Заказчика.'}
                        </Muted>
                      </td>
                    </tr>
                  )}
                  {(summary?.rows ?? []).map((r, i) => (
                    <tr key={`${r.key}-${r.unit ?? ''}-${i}`}>
                      <td className="wrap">{r.key || '— не указано —'}</td>
                      <td className="nowrap">{r.unit}</td>
                      <td className="num">{r.lines.toLocaleString('ru-RU')}</td>
                      <td className="num">{fmtNum(r.fo_total)}</td>
                      <td className="num">{fmtNum(r.fact_cum)}</td>
                      <td className="num">{fmtNum(r.accepted_sk)}</td>
                      <td className="num">{fmtNum(r.id_accepted)}</td>
                      <td className="num">{fmtNum(r.pa_accepted)}</td>
                      <td className="num">{fmtNum(r.nzp)}</td>
                      <td className="num">
                        {r.completion_pct === null ? '' : `${fmtNum(r.completion_pct, 1)} %`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          </>
        )}
      </Panel>

      <Panel>
        <PanelTitle>Сверка: наш учёт ↔ Заказчик</PanelTitle>
        {isNotFound(recError) ? (
          <Banner>Сверка появится после обновления сервера (этап 2 раздела КФО).</Banner>
        ) : recError ? (
          <Banner $tone="danger">{errorText(recError, 'Не удалось выполнить сверку.')}</Banner>
        ) : recLoading ? (
          <Muted>Сверяю…</Muted>
        ) : rec ? (
          <>
            <RecList
              title="Отчитались, но Заказчик не учёл"
              hint="Наши выпущенные «Отчёты СМР» по строке больше, чем Факт СМР накопительно в срезе."
              items={rec.unaccounted}
              oursLabel="Наш отчёт"
              customerLabel="Факт Заказчика"
              onOpenLine={onOpenLine}
            />
            <RecList
              title="ПА Сметчика не совпадает с «Принято ПА» Заказчика"
              hint="Первичные акты в накопительном учёте Сметчика (Ключ ПА = Ключ КФО)."
              items={rec.pa_mismatch}
              oursLabel="ПА Сметчика"
              customerLabel="ПА Заказчика"
              onOpenLine={onOpenLine}
            />
            <RecList
              title="Строки нет в срезе, но есть наш объём"
              hint="Заказчик убрал строку (новая ревизия / новый Ключ), а у нас по ней отчитан объём."
              items={rec.absent_with_volume}
              oursLabel="Наш объём"
              customerLabel="Заказчик"
              onOpenLine={onOpenLine}
            />
          </>
        ) : null}
      </Panel>
    </div>
  );
}

function RecList({
  title,
  hint,
  items,
  oursLabel,
  customerLabel,
  onOpenLine,
}: {
  title: string;
  hint: string;
  items: KfoRecItem[];
  oursLabel: string;
  customerLabel: string;
  onOpenLine: (lineId: string) => void;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h4 style={{ margin: '0 0 4px' }}>
        {title}: {items.length}
      </h4>
      <Muted style={{ marginBottom: 8 }}>{hint}</Muted>
      {items.length === 0 ? (
        <Muted>Расхождений нет.</Muted>
      ) : (
        <TableWrap $maxHeight="36vh">
          <DataTable>
            <thead>
              <tr>
                <th>Ключ</th>
                <th>Шифр ЕР</th>
                <th>Элемент</th>
                <th>Ед.</th>
                <th className="num">{oursLabel}</th>
                <th className="num">{customerLabel}</th>
                <th className="num">Разница</th>
                <th>Примечание</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={`${it.line_id}-${it.note}`}
                  data-click="true"
                  onClick={() => onOpenLine(it.line_id)}
                >
                  <td className="nowrap">{it.ext_key}</td>
                  <td className="nowrap">{it.ep_code}</td>
                  <td className="wrap" style={{ minWidth: 140 }}>
                    {it.element}
                  </td>
                  <td className="nowrap">{it.unit}</td>
                  <td className="num">{fmtNum(it.ours)}</td>
                  <td className="num">{fmtNum(it.customer)}</td>
                  <td className="num">{fmtNum(it.delta)}</td>
                  <td className="wrap">{it.note}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
    </div>
  );
}
